import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPolicyAcceptanceRecords, PolicyAcceptanceRecord } from '@/utils/policyAcceptance';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';

const PolicyAcceptanceRecords = () => {
  const [records, setRecords] = useState<PolicyAcceptanceRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<PolicyAcceptanceRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      
      try {
        // Check if we can use Supabase
        if (supabase) {
          const { data, error } = await supabase
            .from('policy_acceptances')
            .select('*')
            .order('timestamp', { ascending: false });
            
          if (error) {
            console.error('Error fetching policy acceptance records:', error);
            toast.error('Failed to fetch records from database');
            // Fall back to local records if there's an error
            const localRecords = getPolicyAcceptanceRecords();
            setRecords(localRecords);
          } else if (data) {
            // Convert Supabase records to our format
            const formattedRecords: PolicyAcceptanceRecord[] = data.map(record => ({
              id: record.id || uuidv4(),
              userId: record.user_id || '',
              userEmail: record.user_email,
              userType: record.user_type,
              termsAccepted: record.terms_accepted,
              privacyAccepted: record.privacy_accepted,
              cookieAccepted: record.cookie_accepted,
              timestamp: record.timestamp,
              ipAddress: record.ip_address || undefined,
              userAgent: record.user_agent || undefined
            }));
            setRecords(formattedRecords);
          }
        } else {
          // Use local storage for development
          const localRecords = getPolicyAcceptanceRecords();
          setRecords(localRecords);
        }
      } catch (error) {
        console.error('Error loading policy acceptance records:', error);
        toast.error('Error loading records');
        // Fallback to local records
        const localRecords = getPolicyAcceptanceRecords();
        setRecords(localRecords);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  useEffect(() => {
    // Filter records based on search term and user type filter
    let filtered = [...records];
    
    if (userTypeFilter !== "all") {
      filtered = filtered.filter(record => record.userType === userTypeFilter);
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.userEmail.toLowerCase().includes(search) ||
        record.ipAddress?.toLowerCase().includes(search)
      );
    }
    
    setFilteredRecords(filtered);
  }, [records, searchTerm, userTypeFilter]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1">
          <label htmlFor="search" className="text-sm font-medium mb-1 block">
            Search by Email or IP
          </label>
          <Input
            id="search"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <label htmlFor="userType" className="text-sm font-medium mb-1 block">
            User Type
          </label>
          <Select
            value={userTypeFilter}
            onValueChange={setUserTypeFilter}
          >
            <SelectTrigger id="userType">
              <SelectValue placeholder="Filter by user type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="candidate">Candidates</SelectItem>
              <SelectItem value="employer">Employers</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableCaption>List of policy acceptances by users</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="hidden md:table-cell">Policies Accepted</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead className="hidden lg:table-cell">IP Address</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">Loading records...</TableCell>
              </TableRow>
            ) : filteredRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">No records found</TableCell>
              </TableRow>
            ) : (
              filteredRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="font-medium">{record.userEmail}</TableCell>
                  <TableCell>
                    <Badge variant={record.userType === 'candidate' ? 'default' : 'secondary'}>
                      {record.userType === 'candidate' ? 'Candidate' : 'Employer'}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex space-x-1">
                      {record.termsAccepted && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Terms
                        </Badge>
                      )}
                      {record.privacyAccepted && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          Privacy
                        </Badge>
                      )}
                      {record.cookieAccepted && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          Cookies
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(record.timestamp)}</TableCell>
                  <TableCell className="hidden lg:table-cell">{record.ipAddress}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PolicyAcceptanceRecords;
